
import { h, Fragment, render } from 'preact'
import { useState, useRef, useEffect, useContext } from 'preact/hooks'
import { createPortal, createContext } from 'preact/compat'

import { createModel, createUrl } from './utility/model'
import { single, multiple } from './utility/url'
import { json } from './utility/dom'
import { scrollTo } from './utility/scrollTo'

import { Job } from './components/Job'
import { Checkbox } from './components/Checkbox'
import { Tag } from './components/Tag'
import { Pagination } from './components/Pagination'
import { SavedJobsButton } from './components/SavedJobsButton'

import classnames from 'classnames'


const Loading = createContext(false)
const Query = createContext(undefined)
const SiteSettings = createContext({})


export function init() {

	const elements = {
		searchEl : document.getElementById('search-keywords'),
		filterTagsEl : document.getElementById('search-filter-tags'),
		facetsEl : document.getElementById('search-facets'),
		resultsEl : document.getElementById('search-results'),
	}

	if (!elements.searchEl) return

	const siteSettings = json('#site-settings')
	const { homeUrl, searchUrl } = siteSettings

	const query = createModel({
		initial : true,
		search : single('search'),
		status : single('status'),
		specialism : multiple('specialism'),
		region : multiple('region'),
		category : multiple('category'),
		type : multiple('type'),
		engagement : multiple('engagement'),
		ownerId : multiple('ownerId'),
		companyId : multiple('companyId'),
		featured : single('featured'),
		orderBy : single('orderBy'),
		perPage : single('perPage') || 50,
		page : single('page')
	})

	render(<App homeUrl={homeUrl} siteSettings={siteSettings} query={query} {...elements} />, document.body)
}


function App({ searchEl, filterTagsEl, resultsEl, facetsEl, homeUrl, siteSettings, query }) {

	const latestReq = null

	const request = () => {
		const api = createUrl(homeUrl + '/api/jobs', query.getAll(), true)
		const search = createUrl(siteSettings.searchUrl, query.getAll(), false)
		const latestReq = api
		setLoading(true)
		fetch(api).then(response => response.json()).then(response => {
			if (latestReq !== api) return;
			query.set('initial', false)
			setLoading(false)
			setResults(response.data ?? [])
			setPagination(response.pagination ?? {})
			setFacets(response.facets ?? {})
		})
		window.history.replaceState({}, '', search)
	}

	const [results, setResults] = useState([])
	const [facets, setFacets] = useState({})
	const [pagination, setPagination] = useState({})
	const [loading, setLoading] = useState(false)

	const lastEdit = useState(false)
	const useLastEdit = () => lastEdit

	// first load
	useEffect(request, [])

	return <>
		<SiteSettings.Provider value={siteSettings}>
			<Query.Provider value={query}>
				<Loading.Provider value={loading}>
					{ searchEl && createPortal(<Search onUpdate={request} useLastEdit={useLastEdit} />, searchEl) }
					{ filterTagsEl && createPortal(<FilterTags facets={facets} results={results} pagination={pagination} onUpdate={request} />, filterTagsEl) }
					{ facetsEl && createPortal(<Facets facets={facets} results={results} onUpdate={request} useLastEdit={useLastEdit} />, facetsEl) }
					{ resultsEl && createPortal(<Results results={results} pagination={pagination} onUpdate={request} />, resultsEl) }
				</Loading.Provider>
			</Query.Provider>
		</SiteSettings.Provider>
	</>
}

// facet and view helpers

function getFacetGroups(facets, query, { selectedOnly = false, flat = false } = {}) {

	const groups = {
		specialism : { title: 'Sector', multiple: true },
		region : { title: 'Region', multiple: true },
		category : { title: 'Category', multiple: true },
		type : { title: 'Employment', multiple: true },
		ownerId : { title: 'Owner', multiple: true },
		companyId : { title: 'Organisation', multiple: true },
		status : { title: '', multiple: false },
	}

	const allFacets = {
		...facets,
		status: {
			completed: { 
				key: 'status', 
				value: 'completed', 
				displayAs: 'Show closed jobs', 
				show: () => Object.keys(facets).length > 0
			}
		}
	}

	const groupEntries = Object.entries(allFacets).map(([key, facets]) => {

		if (!groups.hasOwnProperty(key)) return null
		const group = Object.assign({}, groups[key])

		group.facets = Object.values(facets)
			.map(facet => {
				const queryVal = group.multiple 
					? (query.get(facet.key) || []) 
					: (query.get(facet.key) || '')
				facet.checked = group.multiple
					? queryVal.indexOf(facet.value) > -1
					: queryVal === facet.value
				return facet
			})
			.filter(facet => {
				if (selectedOnly) return facet.checked
				return facet.checked || facet.count || (facet.show && facet.show())
			})
			.sort((a, b) => a.displayAs < b.displayAs ? -1 : 1)

		if (!group.facets.length) return null
		return [key, group]

	}).filter(Boolean)

	return flat 
		? groupEntries.map(([key, group]) => Object.values(group.facets)).flat()
		: Object.fromEntries(groupEntries)
}

function clearFacet(facet, query) {
	const current = query.get(facet.key)
	if (Array.isArray(current)) {
		query.removeFrom(facet.key, facet.value)
	} else {
		query.set(facet.key, null)
	}
}

function checkboxUpdateQuery(checkbox, query) {
	const { name, value, checked } = checkbox
	const multiple = Array.isArray(query.get(name))
	if (checked) {
		multiple ? query.addTo(name, value) : query.set(name, value)
	} else {
		multiple ? query.removeFrom(name, value) : query.set(name, '')
	}
}

function getStatusText(query, selected, pagination) {

	const total = pagination.totalItems
	if (total === undefined) return ''

	let number = ''
	if (pagination.totalItems && (pagination.currentPage < pagination.lastPage)) {
		number = `${pagination.firstItemOnPage}–${pagination.lastItemOnPage} of ${pagination.totalItems}`
	} else {
		const all = total > 10 ? 'all' : ''
		number = `${all} ${total}`
	}

	const s = pagination.totalItems === 1 ? '' : 's'
	const roles = query.get('status') === 'completed' ? `closed role${s}` : `role${s}`
	
	let text = `Showing ${number} ${roles}`
	return (selected.length > 0) ? `${text} in` : text;
}

// components

function Search({ onUpdate, useLastEdit }) {

	const query = useContext(Query)
	const [lastEdit, setLastEdit] = useLastEdit()

	const handler = event => {
		setLastEdit(event.currentTarget.name)
		query.set('page', null)
		query.set(event.currentTarget.name, event.currentTarget.value)
		onUpdate()
	}

	return <>
		<div class="main-search relative">
			<input 
				autocomplete="off"
				class="main-search__input relative" 
				type="text" 
				name="search" 
				placeholder="Search for roles, e.g. ‘education’ or ‘AQ Ref’" 
				value={query.get('search') || ''} 
				onInput={handler} 
			/>
			<input type="submit" class="main-search__submit" />
		</div>
	</>
}


function FilterTags({ facets, results, pagination, onUpdate }) {

	const query = useContext(Query)
	const loading = useContext(Loading)

	const allSelected = getFacetGroups(facets, query, { selectedOnly: true, flat: true })
	const selected = allSelected.filter(facet => {
		return facet.key !== 'status'
	})

	const statusText = getStatusText(query, selected, pagination)
	const prevStatusText = usePrevious(statusText)

	const removeSearchHandler = event => {
		query.set('search', '')
		onUpdate()
	}

	const closeHandler = facet => event => {
		query.set('page', null)
		clearFacet(facet, query)
		onUpdate()
	}

	const clearAllHandler = event => {
		query.set('page', null)
		query.set('search', null)
		query.set('status', '')
		selected.map(facet => clearFacet(facet, query))
		onUpdate()
	}

	const roles = pagination.totalItems === 1 ? 'role' : 'roles'
	const search = query.get('search')

	return <>
		<div class="button-row">
			<small class="text-white">{loading ? prevStatusText : statusText}</small>
			{search && <Tag title={search} white close smaller onClick={removeSearchHandler} />}
			{selected.map(facet => {
				return <Tag title={facet.displayAs} white close smaller onClick={closeHandler(facet)} />
			})}
			{allSelected.length > 0 && <button type="button" class="button button--fill button--fill-round button--small" onClick={clearAllHandler}>Clear</button>}
		</div>		
	</>
}


function Facets({ facets, results, onUpdate, useLastEdit }) {

	// disjunctive facets
	// remembers the previously-set facets, and copies across the max count
	
	const [lastEdit, setLastEdit] = useLastEdit()
	const previousFacets = usePrevious(facets)

	if (lastEdit && facets[lastEdit]) {
		Object.keys(previousFacets[lastEdit] ?? {}).map(value => {
			if (facets[lastEdit][value] === undefined) {
				facets[lastEdit][value] = { ...previousFacets[lastEdit][value] }	
			} else {
				const previousCount = previousFacets[lastEdit][value].count
				const currentCount = facets[lastEdit][value].count
				facets[lastEdit][value].count = Math.max(previousCount, currentCount)
			}
		})
	}

	// get groups based on disjunctive facets

	const query = useContext(Query)
	const groups = getFacetGroups(facets, query)
	const selected = getFacetGroups(facets, query, { selectedOnly: true, flat: true })
	const hasGroups = Object.keys(groups).length > 0

	// update the model when filters change

	
	
	const onChange = key => event => {
		setLastEdit(key)
		checkboxUpdateQuery(event.currentTarget, query)
		query.set('page', null)
		onUpdate()
	}

	const clearSearch = () => {
		query.set('page', null)
		query.set('search', null)
		selected.map(facet => clearFacet(facet, query))
		onUpdate()
	}

	const count = selected.length
	const [mobileOpen, setMobileOpen] = useState(false)
	const buttonClasses = classnames(
		'button--smaller button--fill button--grey button--icon-left',
		'button--icon-chevron-vertical',
		mobileOpen ? '' : 'down',
		'hide@medium'
	)

	return <>
		{hasGroups && <>
			<div class="flex space-between flex-center padding-bottom-2 hide@medium">
				<span>
					{count} filter{count === 1 ? '' : 's' } applied
				</span>
				<button class={buttonClasses} onClick={() => setMobileOpen(!mobileOpen)}>
					Edit filters
				</button>
			</div>
		</>}

		<div class={mobileOpen ? 'show@medium' : 'hide show@medium'}>
			<div class="filter">
				{Object.entries(groups).map(([key, group]) => {
					return <CheckboxGroup 
						key={key} 
						text={group.title} 
						items={group.facets} 
						onChange={onChange(key, group.multiple)} 
					/>
				}
				)}
			</div>
		</div>

		{hasGroups && <>
			<div class={mobileOpen ? 'show hide@medium' : 'hide hide@medium'}>
				<button class="button--smaller button--fill button--yellow margin-top-3 margin-bottom-2" onClick={() => setMobileOpen(false)}>
					Apply filters
				</button>
				<button class="button--smaller button--outline button--outline-square button--grey margin-top-3 margin-bottom-2 margin-left-2" onClick={clearSearch}>
					Clear filters
				</button>
				<hr class="margin-bottom-5" />
			</div>
		</>}

		{selected.length > 0 && <>
			<div class="hide show@medium">
				<button type="button" class="margin-top-3 margin-bottom-5 button button--small button--outline button--grey button--block" onClick={clearSearch}>Clear search</button>
			</div>
		</>}
	</>
}


function FilterGroup({ title, children }) {
	const [open, setOpen] = useState(true)
	const classes = classnames('filter__section', !open && 'closed')
	return <div class={classes}>
    	{title && <span class="filter__section__title text-dark heading-5">{title}</span>}
		{title && <button onClick={() => setOpen(!open)} class="button button--icon-only button--small filter__section__button" aria-label="Toggle filters"></button>}
    	<div class="filter__section__inner">
    		{children}
    	</div>
    </div>
}


function CheckboxGroup({ text, items, onChange }) {

	const loading = useContext(Loading)
	const query = useContext(Query)

	const fields = items
		.filter(field => field.checked || field.count || (field.show && field.show()))
		.sort((a, b) => a.displayAs < b.displayAs ? -1 : 1)

	return (fields.length > 0) && <>
		<FilterGroup title={text}>
			{fields.map(field => {
				return <Checkbox 
					name={field.key} 
					value={field.value} 
					label={field.displayAs} 
					checked={field.checked}
					additional={field.count}
					onChange={onChange}
					stack
				/>
			})}
		</FilterGroup>
    </>
}


function Results({ results, pagination, onUpdate }) {

	const loading = useContext(Loading)
	const siteSettings = useContext(SiteSettings)
	const query = useContext(Query)

	const numberToUrl = number => createUrl(window.location, {
		...query.getAll(), 
		page : number
	})

	const onPageClick = (event, page) => {
		event.preventDefault()
		if (page.current === true || !page.number || page.disabled) return
		query.set('page', page.number)

		const height = document.documentElement.scrollHeight

		document.body.style.height = `${height}px`
		scrollTo(document.body)
		setTimeout(() => document.body.style.height = '', 1000)
		onUpdate()
	}

	const completed = query.get('status') === 'completed'

	return <div style={loading ? 'opacity:0.5' : ''}>
		{query.get('initial') && <>
			<div class="padding-top-5 padding-bottom-5 text-center" style="position:absolute; top:0; left:0; width:100%;">
				<div class="spinner spinner--large"></div>
				<div class="margin-top-2">Loading</div>
			</div>
		</>}
		{results.map(job => <>
			<Job 
				class="margin-bottom-2" 
				job={job} 
				key={job.Id} 
				searchUrl={siteSettings.searchUrl}
				showLink={!completed}
				statusParam={query.get('status')}
			/>
		</>)}

        {false && results.length > 0 && <div class="cta-box bg--blue padding-y-4 padding-x-4 margin-bottom first-last text-white ">
        	<h3 class="heading-5">Get job alerts for this search</h3>
	        {/*<p>Be the first to find out about roles lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>*/}
            <button class="button button--arrow arrow--left margin-top-3 text-white">Sign up</button>
        </div>}

        {!results.length && !query.get('initial') && <>
        	<div class="cta-box border--lightgrey padding-y-4 padding-x-4 margin-bottom first-last">
	        	<h3 class="heading-5">No roles found for your search</h3>
	            <p>Try another search term or alternatively clear any currently selected filters.</p>
	        </div>
        </>}

		<Pagination {...pagination} onUpdate={onUpdate} numberToUrl={numberToUrl} onClick={onPageClick} />
	</div>
}


// hooks

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}
